import { forwardRef } from "react";

/**
 * Closed 3D Tanakh book.
 * Pure CSS 3D — no WebGL, runs everywhere at 60fps.
 *
 * Hebrew book orientation: spine on LEFT, free edges on RIGHT.
 *
 * Props:
 *   onOpen      — called when the user taps the book
 *   opening     — when true, play the RTL open animation then onOpen completes
 *   size        — "md" | "lg" | "xl" (default "lg")
 *   title       — Hebrew title on cover (default: תנ״ך)
 *   subtitle    — italic subtitle below title (default: TORAHYA)
 *   float       — animated gentle float while idle
 */
const SIZE_MAP = {
  md: { w: 320, h: 440 },
  lg: { w: 420, h: 580 },
  xl: { w: 520, h: 720 },
};

const BookCover = forwardRef(function BookCover(
  {
    onOpen,
    opening = false,
    size = "lg",
    title = "תוריא",
    subtitle = "TORAHYA",
    float = true,
    theme = "navy",   // "navy" (Tanakh) | "teal" (Mishnah) — drives the
                      // cover gradient via [data-theme]. Default keeps
                      // the existing Tanakh look untouched.
  },
  ref
) {
  const dims = SIZE_MAP[size] || SIZE_MAP.lg;

  return (
    <div
      ref={ref}
      className={`book-scene inline-block ${float ? "closed-book-float" : ""}`}
      style={{ width: dims.w, height: dims.h }}
      data-testid="closed-book-scene"
    >
      <div
        className={`closed-book ${opening ? "is-opening" : ""}`}
        data-size={size}
        data-theme={theme}
        onClick={opening ? undefined : onOpen}
        role="button"
        tabIndex={0}
        aria-label="פתח את הספר"
        data-testid="closed-book"
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !opening) {
            e.preventDefault();
            onOpen?.();
          }
        }}
        style={{ width: dims.w, height: dims.h }}
      >
        <div className="face cover-front">
          <div className="cover-frame" />
          <h2 className="cover-title">{title}</h2>
          <p className="cover-sub">{subtitle}</p>
          <div className="cover-ornament">✦ ❖ ✦</div>
        </div>
        <div className="face cover-back" />
        <div className="spine" />
        <div className="pages-edge" />
        <div className="pages-edge-top" />
        <div className="pages-edge-bottom" />
      </div>
      <div className="closed-book-shadow" />
    </div>
  );
});

export default BookCover;
